import * as Sentry from "@sentry/react";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQuery, useQueryClient } from "react-query";

import { getDefaultAvatar, getUserAvatars, getUserMetadata } from "../api";
import { getBlastUserMetadata } from "../api/blast";
import { lifetimeDataQuerySetting } from "../components/customQueryClientProvider";
import { Preloader } from "../components/preloader";
import { PAGE_SIZE } from "../constants";
import { ServerError } from "../pages/authPages";
import { OrgAndPermsDto, UserDto } from "../types/dto";
import { getIsSysAdmin, isBlast } from "../utils";
import {
	newOrgIdLS,
	selectedOrgIdLS,
	setSelectedOrgLS,
} from "../utils/localStorageUtils";
import { SiteContextProvider } from "./siteContext";
import { DataOrgAndPermsDto } from "../types/blastDtos";

const blankOrg = {
	id: "",
	name: "",
	description: "",
	avatar_Base64: "",
	customData: {},
	permissions: {
		isOrgOwner: false,
		isOrgAdmin: false,
		isDeveloper: false,
		isBusinessManager: false,
		isSubManager: false,
		isProductOwnerEnabled: false,
		isServiceOwnerEnabled: false,
	},
};

export const UserDataContext = createContext<{
	userProfile: UserDto;
	userOrgs: OrgAndPermsDto[] | DataOrgAndPermsDto[];
	selectedOrg: OrgAndPermsDto | DataOrgAndPermsDto;
	selectedOrgId: string;
	setNewSelectedOrg: any;
	refreshUserData: any;
}>({
	userProfile: { firstName: "", lastName: "", id: "", email: "" },
	userOrgs: [] as OrgAndPermsDto[],
	selectedOrg: blankOrg,
	selectedOrgId: "",
	setNewSelectedOrg: (_: any) => {},
	refreshUserData: () => {},
});

export const manageUserData = async (newSelectedOrgId?: string) => {
	const { orgs }: { orgs: Array<OrgAndPermsDto> } = await (isBlast
		? getBlastUserMetadata
		: getUserMetadata)();

	if (orgs.length) {
		const orgIdToMatch =
			newOrgIdLS() || newSelectedOrgId || selectedOrgIdLS();

		const foundOrg = orgs.find((org) => org.id === orgIdToMatch);

		let newUserOrgs = [...orgs];
		let newSelectedOrg: OrgAndPermsDto;

		if (foundOrg) {
			newSelectedOrg = foundOrg;
			newUserOrgs = newUserOrgs.filter((org) => org.id !== foundOrg.id);
		} else {
			newSelectedOrg = newUserOrgs.shift() as OrgAndPermsDto;
		}
		const { newSelectedOrg: selectedOrg, newUserOrgs: userOrgs } =
			setSelectedOrgLS(newSelectedOrg, newUserOrgs);
		return { orgs, selectedOrg, userOrgs };
	}

	return {
		orgs,
		selectedOrg: blankOrg,
		userOrgs: [],
	};
};

export const UserDataProvider = ({
	children,
	isReturnIfNotAuth = false,
	isWaitForUser = false,
}: {
	children: any;
	isReturnIfNotAuth: boolean;
	isWaitForUser: boolean;
}) => {
	const { isAuthenticated, user: authUser } = useAuth();

	const [selectedOrg, setSelectedOrg] = useState<OrgAndPermsDto>(blankOrg);
	const [userOrgs, setUserOrgs] = useState<OrgAndPermsDto[]>([]);

	const setNewSelectedOrg = (newOrgToSelect: OrgAndPermsDto) => {
		const { newSelectedOrg, newUserOrgs } =
			setSelectedOrgLS(newOrgToSelect);
		setSelectedOrg(newSelectedOrg);
		setUserOrgs(newUserOrgs);
	};

	const { isLoading, error, data } = useQuery(
		Object.keys({ manageUserData })[0],
		() => manageUserData(),
		{
			...lifetimeDataQuerySetting,
			enabled:
				isAuthenticated ||
				import.meta.env.VITE_MOCK_MODE === "true" ||
				import.meta.env.MODE === "test",
		}
	);

	useEffect(() => {
		if (data) {
			setSelectedOrg(data.selectedOrg);
			setUserOrgs(data.userOrgs);
		}
	}, [data]);

	const queryClient = useQueryClient();

	useEffect(() => {
		queryClient.invalidateQueries({
			predicate: (query) =>
				Array.isArray(query.queryKey)
					? query.queryKey.find((key) => key === "selectedOrgId")
					: false,
		});
	}, [selectedOrg, queryClient]);

	const refreshUserData = () => {
		queryClient.invalidateQueries(Object.keys({ manageUserData })[0]);
	};

	const userProfile = {
		firstName: authUser?.profile.given_name || "",
		lastName: authUser?.profile.family_name || "",
		id: authUser?.profile.sub || "",
		email: authUser?.profile.email || "",
		avatar_Base64: undefined,
	};

	const { data: userProfilePic } = useQuery(
		[Object.keys({ getUserAvatars })[0], userProfile.id],
		() => getUserAvatars([userProfile.id]),
		{
			...lifetimeDataQuerySetting,
			enabled:
				isAuthenticated ||
				import.meta.env.VITE_MOCK_MODE === "true" ||
				import.meta.env.MODE === "test",
		}
	);
	const { data: defaultAvatar } = useQuery(
		[Object.keys({ getDefaultAvatar })[0]],
		() => getDefaultAvatar(),
		{
			...lifetimeDataQuerySetting,
		}
	);

	userProfile.avatar_Base64 =
		userProfilePic?.avatars[0]?.avatar_Base64 || defaultAvatar;

	if (
		isReturnIfNotAuth ||
		(import.meta.env.MODE === "test" && !isWaitForUser)
	) {
		return (
			<UserDataContext.Provider
				value={{
					userProfile,
					userOrgs,
					selectedOrg,
					setNewSelectedOrg,
					selectedOrgId: selectedOrg.id,
					refreshUserData,
				}}
			>
				<SiteContextProvider>{children}</SiteContextProvider>
			</UserDataContext.Provider>
		);
	}

	if (error) {
		return <ServerError error={error} />;
	}

	if (isLoading || (!selectedOrg.id && data?.orgs.length)) {
		return <Preloader />;
	}

	if ((data && authUser) || import.meta.env.MODE === "test") {
		Sentry.setUser({
			name: userProfile.firstName + " " + userProfile.lastName,
			id: userProfile.id,
			email: userProfile.email,
			selectedOrg: {
				id: selectedOrg.id,
				name: selectedOrg.name,
				perms: selectedOrg.permissions,
			},
			// Add any other user properties
			userAgent: navigator.userAgent,
			language: navigator.language,
			platform: navigator.platform,
			cookieEnabled: navigator.cookieEnabled,
		});

		// Add authentication-related tags
		Sentry.setTag("authenticated", "true");

		return (
			<UserDataContext.Provider
				value={{
					userProfile,
					userOrgs,
					selectedOrg,
					setNewSelectedOrg,
					selectedOrgId: selectedOrg.id,
					refreshUserData,
				}}
			>
				<SiteContextProvider>{children}</SiteContextProvider>
			</UserDataContext.Provider>
		);
	}

	return <ServerError message={"No data found"} />;
};
